import React, { memo, useRef } from 'react';
import { Artwork } from '../data/artworks';
import { getWindowTheme } from '../config/windowTheme';
import { useContainerNarrow } from '../hooks/useContainerNarrow';

interface ArtworkDetailWindowProps {
  artwork: Artwork;
  isDarkMode?: boolean;
}

const ArtworkDetailWindow = memo<ArtworkDetailWindowProps>(({ artwork, isDarkMode = false }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const isNarrow = useContainerNarrow(rootRef, 640);
  const t = getWindowTheme(isDarkMode);

  const formattedDate = new Date(artwork.date + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const showDescription =
    artwork.description.trim().length > 0 &&
    artwork.description.trim().toLowerCase() !== artwork.title.trim().toLowerCase();

  const sourceLabel =
    artwork.source === 'local' ? 'NEW' : artwork.source === 'twitter' ? '𝕏' : null;

  const imageBg = isDarkMode ? 'bg-black/30' : 'bg-black/[0.03]';

  const actions = (
    <div className={`flex flex-col gap-2 ${isNarrow ? '' : 'sm:flex-row sm:flex-wrap'}`}>
      <a
        href={artwork.image}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center w-full px-4 py-2.5 rounded text-sm font-medium transition-colors ${t.cta} ${
          isNarrow ? '' : 'sm:w-auto sm:py-2'
        }`}
      >
        Open full size
      </a>
      {artwork.tweetUrl && (
        <a
          href={artwork.tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center justify-center w-full px-4 py-2.5 rounded text-sm font-medium border transition-colors ${t.ctaOutline} ${
            isNarrow ? '' : 'sm:w-auto sm:py-2'
          }`}
        >
          View on 𝕏
        </a>
      )}
    </div>
  );

  return (
    <div ref={rootRef} className={`min-h-full ${t.bg}`}>
      <header className={`${t.headerBar} ${t.headerBg}`}>
        <div className="px-3 sm:px-5 py-2.5 sm:py-3">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <time dateTime={artwork.date} className={t.meta}>
              {formattedDate}
            </time>
            {sourceLabel && (
              <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded border ${t.pillActive}`}>
                {sourceLabel}
              </span>
            )}
          </div>
          <h1 className={`font-black text-base sm:text-xl leading-tight tracking-tight ${t.head}`}>
            {artwork.title}
          </h1>
        </div>
      </header>

      {isNarrow ? (
        <article className="px-3 sm:px-4 py-4 pb-6 space-y-4">
          <figure className={`rounded overflow-hidden border ${imageBg} ${t.border}`}>
            <img
              src={artwork.image}
              alt={artwork.title}
              className="block w-full h-auto max-h-[min(58vh,32rem)] object-contain mx-auto select-none"
              draggable={false}
              decoding="async"
            />
          </figure>

          {showDescription && (
            <p className={`text-sm leading-relaxed ${t.body}`}>{artwork.description}</p>
          )}

          {artwork.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {artwork.tags.map(tag => (
                <span key={tag} className={`px-2 py-0.5 rounded text-[10px] ${t.tag}`}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className={`pt-4 border-t ${t.divider}`}>{actions}</div>

          <p className={t.meta}>
            {artwork.source === 'local' ? 'Local work' : artwork.source === 'twitter' ? 'From 𝕏' : 'Digital art'}
            {' · '}
            {formattedDate}
          </p>
        </article>
      ) : (
        <div className="flex flex-row items-start gap-0">
          <figure className={`flex-1 min-w-0 flex items-center justify-center p-4 lg:p-6 ${imageBg}`}>
            <div className={`w-full rounded overflow-hidden border ${t.border}`}>
              <img
                src={artwork.image}
                alt={artwork.title}
                className="block w-full h-auto max-h-[calc(100vh-12rem)] object-contain mx-auto select-none"
                draggable={false}
                decoding="async"
              />
            </div>
          </figure>

          <aside
            className={`w-[min(100%,20rem)] xl:w-[34%] flex-shrink-0 flex flex-col gap-4 p-5 border-l ${t.border} ${t.surface}`}
          >
            {showDescription && (
              <p className={`text-sm leading-relaxed ${t.body}`}>{artwork.description}</p>
            )}

            {artwork.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {artwork.tags.map(tag => (
                  <span key={tag} className={`px-2 py-0.5 rounded text-xs ${t.tag}`}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className={`pt-4 border-t ${t.divider}`}>{actions}</div>

            <p className={t.meta}>
              {artwork.source === 'local' ? 'Local work' : artwork.source === 'twitter' ? 'From 𝕏' : 'Digital art'}
              {' · '}
              {formattedDate}
            </p>
          </aside>
        </div>
      )}
    </div>
  );
});

ArtworkDetailWindow.displayName = 'ArtworkDetailWindow';

export default ArtworkDetailWindow;
